<template>
  <v-dialog
    v-model="show"
    :persistent="loading"
    width="500"
  >
    <v-card>
      <v-form
        v-model="validForm"
        :disabled="loading"
        @submit.prevent="submit"
      >
        <v-toolbar flat color="grey lighten-2">
          <v-card-title class="headline">Reset password</v-card-title>
          <v-spacer />
          <v-btn
            :disabled="loading"
            icon
            class="mr-0"
            @click="show = false"
          >
            <v-icon>close</v-icon>
          </v-btn>
        </v-toolbar>

        <v-card-text>
          <template v-if="currentStep === RecoveryStep.SendCode">
            <v-text-field
              v-model="steps.sendCode.formData.email"
              :rules="steps.sendCode.rules.email"
              type="email"
              label="Email"
              outlined
              color="blue darken-2"
              hide-details
            />
            <div v-if="steps.sendCode.errorMessage" class="error-container">
              {{ steps.sendCode.errorMessage }}
            </div>
          </template>

          <template v-if="currentStep === RecoveryStep.ChangePassword">
            <v-text-field
              v-model="steps.changePassword.formData.email"
              :rules="steps.changePassword.rules.email"
              type="email"
              label="Email"
              outlined
              color="blue darken-2"
            />
            <v-text-field
              v-model="steps.changePassword.formData.code"
              :rules="steps.changePassword.rules.code"
              label="Code"
              outlined
              color="blue darken-2"
            />
            <v-text-field
              v-model="steps.changePassword.formData.password"
              :rules="steps.changePassword.rules.password"
              type="email"
              label="New password"
              outlined
              color="blue darken-2"
            />
            <div v-if="steps.changePassword.errorMessage" class="error-container">
              {{ steps.changePassword.errorMessage }}
            </div>
          </template>
        </v-card-text>

        <v-divider />

        <v-card-actions v-if="currentStep === RecoveryStep.SendCode">
          <v-btn
            :loading="loading"
            :disabled="!validForm"
            type="submit"
            large
            block
            color="blue darken-2"
            class="white--text"
          >
            Send code
          </v-btn>
        </v-card-actions>
        <v-card-actions v-else-if="currentStep === RecoveryStep.ChangePassword">
          <v-btn
            :loading="loading"
            :disabled="!validForm"
            large
            text
            class="mr-3"
            @click="goToStep(RecoveryStep.SendCode)"
          >
            Back to Send Code
          </v-btn>
          <v-spacer />
          <v-btn
            :loading="loading"
            :disabled="!validForm"
            type="submit"
            large
            color="blue darken-2"
            class="white--text ml-3"
          >
            Change Password
          </v-btn>
        </v-card-actions>
      </v-form>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import { emailRegex, passwordRegex } from '@/helpers/regex';

const RecoveryStep = {
  SendCode: 'SendCode',
  ChangePassword: 'ChangePassword',
};

export default {
  name: 'ForgotPassword',
  data() {
    return {
      RecoveryStep,
      currentStep: RecoveryStep.SendCode,
      steps: {
        sendCode: {
          formData: {
            email: '',
          },
          rules: {
            email: [
              v => !!v || 'Required field',
              v => emailRegex.test(v) || 'Invalid email format',
            ],
          },
          errorMessage: '',
        },
        changePassword: {
          formData: {
            email: '',
            code: '',
            password: '',
          },
          rules: {
            email: [v => !!v || 'Required field'],
            code: [v => !!v || 'Required field'],
            password: [
              v => !!v || 'Required field',
              v => v.length >= 8 || 'Min length: 8',
              v => passwordRegex.test(v)
                || 'Password must be at least 8 characters long and contain at least one lowercase letter, uppercase letter, number and symbol',
            ],
          },
        },
        errorMessage: '',
      },
      validForm: false,
      loading: false,
    };
  },
  computed: {
    ...mapGetters([
      'getForgotPasswordInModalState',
    ]),
    show: {
      get() {
        return this.getForgotPasswordInModalState;
      },
      set(value) {
        this.setForgotPasswordModalState(value);
      },
    },
  },
  methods: {
    ...mapActions([
      'setForgotPasswordModalState',
    ]),
    ...mapActions('parent', [
      'sendRecoveryCode',
      'recoverPassword',
    ]),
    submit() {
      if (this.currentStep === RecoveryStep.SendCode) {
        this.submitSendCode();
      }
      if (this.currentStep === RecoveryStep.ChangePassword) {
        this.submitChangePassword();
      }
    },
    goToStep(stepName) {
      this.currentStep = stepName;
    },
    async submitSendCode() {
      try {
        this.loading = true;
        await this.sendRecoveryCode(this.steps.sendCode.formData.email);
        this.steps.changePassword.formData.email = this.steps.sendCode.formData.email;

        this.goToStep(RecoveryStep.ChangePassword);
      } catch (error) {
        this.steps.sendCode.errorMessage = error.response.data.message;
      } finally {
        this.loading = false;
      }
    },
    async submitChangePassword() {
      try {
        this.loading = true;
        await this.recoverPassword(this.steps.changePassword.formData);
        this.show = false;
      } catch (error) {
        this.steps.changePassword.errorMessage = error.response.data.message;
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.error-container {
  color: #cc0000;
  font-size: 16px;
  padding: 20px 8px 8px;
}
</style>
